import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {useAuth} from './AuthContext';
import { UserApiService, BirthInfo, ConsultationData, ConsultationResponse } from '../services/userApiService';
import * as iztro from 'iztro';

interface UserContextType {
  userProfile: any;
  astrolabe: any;
  birthInfo: any;
  consultationHistory: any[];
  loading: boolean;
  error: string | null;
  updateUserProfile: (data: any) => Promise<void>;
  updateBirthInfo: (data: BirthInfo) => Promise<any>;
  generateAstrolabe: (birthData: any) => any;
  saveConsultation: (
    question: string,
    answer: string,
    category?: 'general' | 'career' | 'love' | 'health' | 'wealth',
    responseData?: any
  ) => Promise<ConsultationResponse | null>;
  deleteConsultation: (id: string) => Promise<void>;
  refreshData: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({children}) => {
  const {user} = useAuth();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [astrolabe, setAstrolabe] = useState<any>(null);
  const [birthInfo, setBirthInfo] = useState<any>(null);
  const [consultations, setConsultations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // 加载用户资料
      const profile = await UserApiService.getUserProfile();
      setUserProfile(profile);
      
      // 加载命盘数据
      const astrolabeData = await UserApiService.getAstrolabe();
      if (astrolabeData) {
        setBirthInfo(astrolabeData.birthInfo);
        setAstrolabe(astrolabeData.astrolabe);
      }
      
      // 加载咨询历史
      const consultationData = await UserApiService.getConsultations();
      setConsultations(consultationData.consultations);
      
    } catch (err: any) {
      setError(err.message);
      console.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateAstrolabe = (birthData: any) => {
    try {
      const {birthDate, birthTime, gender, isLunar} = birthData;
      
      // Convert birthTime to timeIndex (0-12)
      // 时辰对应表：早子时(0:00-1:00)=0, 丑时(1:00-3:00)=1, 寅时(3:00-5:00)=2, 卯时(5:00-7:00)=3,
      // 辰时(7:00-9:00)=4, 巳时(9:00-11:00)=5, 午时(11:00-13:00)=6, 未时(13:00-15:00)=7,
      // 申时(15:00-17:00)=8, 酉时(17:00-19:00)=9, 戌时(19:00-21:00)=10, 亥时(21:00-23:00)=11, 晚子时(23:00-0:00)=12
      let timeIndex = 0;
      
      const convertHourToTimeIndex = (hour: number): number => {
        if (hour >= 0 && hour < 1) return 0;  // 早子时 00:00-01:00
        else if (hour >= 1 && hour < 3) return 1;  // 丑时 01:00-03:00
        else if (hour >= 3 && hour < 5) return 2;  // 寅时 03:00-05:00
        else if (hour >= 5 && hour < 7) return 3;  // 卯时 05:00-07:00
        else if (hour >= 7 && hour < 9) return 4;  // 辰时 07:00-09:00
        else if (hour >= 9 && hour < 11) return 5; // 巳时 09:00-11:00
        else if (hour >= 11 && hour < 13) return 6; // 午时 11:00-13:00
        else if (hour >= 13 && hour < 15) return 7; // 未时 13:00-15:00
        else if (hour >= 15 && hour < 17) return 8; // 申时 15:00-17:00
        else if (hour >= 17 && hour < 19) return 9; // 酉时 17:00-19:00
        else if (hour >= 19 && hour < 21) return 10; // 戌时 19:00-21:00
        else if (hour >= 21 && hour < 23) return 11; // 亥时 21:00-23:00
        else if (hour >= 23) return 12; // 晚子时 23:00-00:00
        return 0;
      };
      
      console.log('Debug birthTime conversion:', {
        birthTime,
        type: typeof birthTime,
        includes_colon: typeof birthTime === 'string' ? birthTime.includes(':') : false
      });
      
      if (typeof birthTime === 'string') {
        // Check if it's a time format like "10:30" or "14:30"
        if (birthTime.includes(':')) {
          const [hourStr] = birthTime.split(':');
          const hour = parseInt(hourStr, 10);
          console.log('Parsed hour from time string:', { hourStr, hour });
          if (!isNaN(hour) && hour >= 0 && hour <= 23) {
            timeIndex = convertHourToTimeIndex(hour);
            console.log('Converted timeIndex:', timeIndex);
          }
        } else {
          // If birthTime is a Chinese time name, convert it
          const timeMap: {[key: string]: number} = {
            '早子时': 0, '子时': 0, '丑时': 1, '寅时': 2, '卯时': 3, '辰时': 4, '巳时': 5,
            '午时': 6, '未时': 7, '申时': 8, '酉时': 9, '戌时': 10, '亥时': 11, '晚子时': 12
          };
          timeIndex = timeMap[birthTime] ?? 0;
          console.log('Chinese time name conversion:', { birthTime, timeIndex });
        }
      } else if (typeof birthTime === 'number') {
        // If birthTime is hour (0-23), convert to timeIndex
        if (birthTime >= 0 && birthTime <= 23) {
          timeIndex = convertHourToTimeIndex(birthTime);
          console.log('Number hour conversion:', { birthTime, timeIndex });
        } else {
          // If birthTime is already a timeIndex (0-12), use it directly
          timeIndex = Math.max(0, Math.min(12, birthTime));
          console.log('Direct timeIndex:', { birthTime, timeIndex });
        }
      }
      
      console.log('Final timeIndex result:', timeIndex);
      
      let astrolabeData;
      if (isLunar) {
        astrolabeData = iztro.astro.byLunar(
          birthDate,
          timeIndex,
          gender,
          false, // isLeapMonth
          true, // fixLeapMonth
          'zh-CN'
        );
      } else {
        astrolabeData = iztro.astro.bySolar(
          birthDate,
          timeIndex,
          gender,
          true, // fixLeapMonth
          'zh-CN'
        );
      }
      
      setAstrolabe(astrolabeData);
      return astrolabeData;
    } catch (err: any) {
      setError('Failed to generate astrolabe: ' + err.message);
      console.error('Error generating astrolabe:', err);
      return null;
    }
  };

  const updateBirthInfo = async (birthData: BirthInfo) => {
    if (!user) return null;
    
    try {
      setLoading(true);
      setError(null);
      
      // 先本地生成命盘（将 BirthInfo 转换为命盘输入格式），再与出生信息一起保存
      const pad = (n: number) => n.toString().padStart(2, '0');
      const astroInput = {
        birthDate: `${birthData.year}-${pad(birthData.month)}-${pad(birthData.day)}`,
        birthTime: `${pad(birthData.hour)}:${pad(birthData.minute)}`,
        gender: birthData.gender,
        isLunar: birthData.calendar === 'lunar',
      };
      const localAstrolabe = generateAstrolabe(astroInput);
      // 保存出生信息并生成命盘（服务端存储并返回）
      const saved = await UserApiService.saveBirthInfo(birthData, localAstrolabe);
      
      setBirthInfo(saved.birthInfo);
      setAstrolabe(saved.astrolabe);
      
      return saved.astrolabe;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const saveConsultation = async (
    question: string,
    answer: string,
    category: 'general' | 'career' | 'love' | 'health' | 'wealth' = 'general',
    responseData?: any
  ) => {
    if (!user) return null;
    
    try {
      const consultationData: ConsultationData = {
        question,
        answer,
        consultationType: category,
      };
      
      const newConsultation = await UserApiService.saveConsultation(consultationData);
      
      setConsultations(prev => [newConsultation, ...(prev || [])]);
      
      return newConsultation;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteConsultation = async (consultationId: string) => {
    try {
      const idNum = parseInt(consultationId, 10);
      await UserApiService.deleteConsultation(idNum);
      
      setConsultations(prev => 
        (prev || []).filter(consultation => consultation.id !== idNum)
      );
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const updateUserProfile = async (data: any) => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const updatedProfile = await UserApiService.updateUserProfile(data);
      
      setUserProfile(updatedProfile);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await loadUserData();
  };

  const value = {
    userProfile,
    astrolabe,
    birthInfo,
    consultationHistory: consultations,
    loading,
    error,
    updateUserProfile,
    updateBirthInfo,
    generateAstrolabe,
    saveConsultation,
    deleteConsultation,
    refreshData,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};